import { useContext } from 'react'

import { MobXProviderContext, useLocalObservable } from 'mobx-react'

import { DefaultCompanyType } from '~/client/graph'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import Company from '~/client/src/shared/models/Company'
import { ITag } from '~/client/src/shared/models/Tag'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import WhiteListItemsStore from '~/client/src/shared/stores/domain/WhiteListItems.store'
import {
  LOAD_AND_LISTEN_TO_COMPANIES,
  LOAD_AND_LISTEN_TO_COMPANY_TYPE_TAGS,
} from '~/client/src/shared/stores/EventStore/eventConstants'

import { CompanyClassification, CompanyStatus, ICompanyRow } from '../types'

interface ProvidedStores {
  companiesStore: CompaniesStore
  tagsStore: TagsStore
  whiteListItemsStore: WhiteListItemsStore
  state: DesktopInitialState
}

export interface CompanyRecordsResult {
  rows: readonly ICompanyRow[]
  loading: boolean
}

const LOADING_EVENTS = [
  LOAD_AND_LISTEN_TO_COMPANIES,
  LOAD_AND_LISTEN_TO_COMPANY_TYPE_TAGS,
]

function resolveTags(tagsStore: TagsStore, ids: string[]): ITag[] {
  return ids.flatMap(id => {
    const tag = tagsStore.getTagById(id)
    return tag ? [tag] : []
  })
}

function resolveCompanyTypeTags(
  tagsStore: TagsStore,
  company: Company,
): ITag[] {
  return resolveTags(tagsStore, company.typeTags).sort((a, b) =>
    a.name.localeCompare(b.name),
  )
}

export function useCompanyRecords(): CompanyRecordsResult {
  const { companiesStore, tagsStore, whiteListItemsStore, state } = useContext(
    MobXProviderContext,
  ) as unknown as ProvidedStores

  const local = useLocalObservable(() => ({
    get rows(): readonly ICompanyRow[] {
      const hasVendorType = !!companiesStore.getCompanyTypeTagByType(
        DefaultCompanyType.Vendor,
      )
      const membersByCompanyId = tagsStore.allUsersByRelatedTagIdMap

      return companiesStore.allCompanies.map(company => {
        const members = membersByCompanyId[company.id] || []

        return {
          id: company.id,
          name: company.name || '',
          code: company.code || '',
          avatarUrl: company.avatarUrl || '',
          whitelistedDomains: whiteListItemsStore
            .getItemsByCompanyId(company.id)
            .map(item => item.domainName),
          roleTags: resolveTags(tagsStore, company.roleIds),
          tradeTags: resolveTags(tagsStore, company.tradeIds),
          typeTags: resolveCompanyTypeTags(tagsStore, company),
          responsibleUsers: members.filter(member =>
            company.responsibleContactIds.includes(member.id),
          ),
          membersCount: members.length,
          status: company.isDeleted
            ? CompanyStatus.Deactivated
            : CompanyStatus.Active,
          classification:
            hasVendorType && companiesStore.isVendorOnlyCompany(company.id)
              ? CompanyClassification.VendorOnly
              : CompanyClassification.Common,
        }
      })
    },
  }))

  const loading = LOADING_EVENTS.some(event => state.loading.get(event))

  return { rows: local.rows, loading }
}
